// The public demo's window — `/v1/me/demo` (spec/24 §3, F-DEM-01, ADR-0039).
//
//   GET   /v1/me/demo   the caller's demo window, if they have had one: when it started, when it ends, and
//                       when another may be started
//   POST  /v1/me/demo   start one, or return the one still running
//
// The demo at `demo.{domain}` is the venue console and the kiosk running on a **simulated venue in the
// visitor's browser** — dummy data, nothing in the database (spec/24 §2). The only thing the server keeps is
// this: one row per person saying when their demo started and when it ends. A signed-in Clavian account is
// the gate; the demo front-ends read the window on load and show "Your demo has ended" once it has.
//
// A window lasts 15 minutes and a new one may start 24 hours after the last one started (the deployment's
// `DEMO_WINDOW_MINUTES` and `DEMO_WINDOW_COOLDOWN_HOURS`). Starting while one runs returns it unchanged
// (`200`); starting one fresh answers `201`; starting another too soon is `429 demo-limit-reached`, with
// `Retry-After` saying when the next may start. Nothing about a venue, a station or a session is read or
// written by either route.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
const SIGNED_IN = 'Needs a signed-in user: a session cookie in a browser, `Authorization: Bearer` in the apps. It is about the ' +
    'caller only.';
/** Whether a window is still running at `serverTime`. */
export const DEMO_WINDOW_STATUSES = ['active', 'ended'];
export const DemoWindowStatusSchema = z.enum(DEMO_WINDOW_STATUSES).openapi('DemoWindowStatus', {
    description: '`active` until `endsAt`, `ended` from then on.',
});
export const DemoWindowSchema = z
    .object({
    startedAt: IsoDateTimeSchema.openapi({ description: 'When this demo started.' }),
    endsAt: IsoDateTimeSchema.openapi({ description: 'When it ends: the demo front-ends stop at this instant.' }),
    /** The earliest instant `POST /v1/me/demo` starts a new window, rather than refusing one. */
    nextStartAt: IsoDateTimeSchema.openapi({
        description: 'The earliest a new demo may be started: the deployment’s cool-down after `startedAt`.',
    }),
    status: DemoWindowStatusSchema,
})
    .openapi('DemoWindow', { description: 'One person’s demo window (spec/24 §3).' });
export const DemoAccessSchema = z
    .object({
    /** The caller's latest window, running or ended; `null` for someone who has never started one. */
    window: DemoWindowSchema.nullable(),
    /**
     * The server's clock when it answered. A front-end counts the window down against this rather than the
     * device's own clock, which may be minutes out.
     */
    serverTime: IsoDateTimeSchema,
})
    .openapi('DemoAccess', { description: 'The caller’s demo window and the server’s clock.' });
export const getMyDemoRoute = createRoute({
    method: 'get',
    path: '/v1/me/demo',
    operationId: 'getMyDemo',
    tags: ['me'],
    summary: 'Your demo window',
    description: `${SIGNED_IN} The caller's latest demo window — running or ended — or \`null\` when they have never started ` +
        'one, with the server’s clock. Reading it starts nothing.',
    responses: {
        200: jsonResponse(DemoAccessSchema, "The caller's demo window."),
        401: commonProblemResponses[401],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const startMyDemoRoute = createRoute({
    method: 'post',
    path: '/v1/me/demo',
    operationId: 'startMyDemo',
    tags: ['me'],
    summary: 'Start your demo',
    description: `${SIGNED_IN} Starts the caller's demo window, or returns the one still running unchanged. A new window may ` +
        'start once the cool-down after the last one’s start has passed; until then the answer is ' +
        '`429 demo-limit-reached` with `Retry-After`. Takes no body.',
    responses: {
        200: jsonResponse(DemoAccessSchema, 'A window was already running: it is returned unchanged.'),
        201: jsonResponse(DemoAccessSchema, 'A new window started.'),
        401: commonProblemResponses[401],
        429: problemResponse('A demo was had too recently, or too many requests.', 'demo-limit-reached', 'rate-limited'),
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=demo.js.map